import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { SinatraState } from '@rsos/sinatra';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { organizationSelector } from '../selectors/organizationSelector';
import { KeywordAPI } from './KeywordAPI';
import type { KeywordTier } from './KeywordTier';

/**
 * Because of how keywords are currently stored on the backend, in a json blob,
 * there are actually only two true actions: fetching and updating the entire
 * list of tiers.
 */
export const fetchKeywordTiers = createAsyncThunk<
  KeywordTier[],
  void,
  { state: { sinatra: SinatraState } }
>('appSettings/fetchKeywordTiers', async (_, { rejectWithValue, getState }) => {
  const orgName = organizationSelector(getState())?.name;
  if (!orgName) return rejectWithValue('No organization to fetch from');
  try {
    const api = new KeywordAPI(orgName);
    return api.fetchKeywordTiers();
  } catch (err) {
    const { message } = normalizeError(err);
    return rejectWithValue(message);
  }
});

export const updateKeywordTiers = createAsyncThunk<
  KeywordTier[],
  KeywordTier[],
  { state: { sinatra: SinatraState } }
>(
  'appSettings/updateKeywordTiers',
  async (keywordTiers, { rejectWithValue, getState, dispatch }) => {
    const orgName = organizationSelector(getState())?.name;
    if (!orgName) return rejectWithValue('No organization to fetch from');
    try {
      // This eslint disable was discussed extensively in code review and
      // decided to leave it as is. See
      // https://github.com/RapidSOS/milky-way/pull/25400/files/08b789494fd530d8f9fb3c2a4d11c82a180dd471#r2201203118
      //
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      dispatch(keywordTiersSlice.actions.optimisticUpdate(keywordTiers));
      const api = new KeywordAPI(orgName);
      return api.updateKeywordTiers(keywordTiers);
    } catch (err) {
      const { message } = normalizeError(err);
      dispatch(fetchKeywordTiers());
      return rejectWithValue(message);
    }
  },
);

export type KeywordTiersState = {
  loading: boolean;
  loadingError: null | string;
  saving: boolean;
  savingError: null | string;
  keywordTiers?: KeywordTier[];
};

const initialState: KeywordTiersState = {
  loading: false,
  loadingError: null,
  saving: false,
  savingError: null,
  keywordTiers: undefined,
};

const keywordTiersSlice = createSlice({
  name: 'keywordTiers',
  initialState,
  reducers: {
    optimisticUpdate(state, action) {
      state.keywordTiers = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchKeywordTiers.fulfilled, (state, action) => {
        state.keywordTiers = [...action.payload];
        state.loading = false;
        state.loadingError = null;
      })
      .addCase(fetchKeywordTiers.pending, state => {
        state.loading = true;
      })
      .addCase(fetchKeywordTiers.rejected, (state, { error = {} }) => {
        state.loading = false;
        state.loadingError =
          error.message || 'Loading keyword notification settings failed.';
      })

      .addCase(updateKeywordTiers.fulfilled, state => {
        state.saving = false;
        state.savingError = null;
      })
      .addCase(updateKeywordTiers.pending, state => {
        state.saving = true;
      })
      .addCase(updateKeywordTiers.rejected, (state, { error = {} }) => {
        state.saving = false;
        state.loadingError =
          error.message || 'Saving keyword notification settings failed.';
      });
  },
});

export const keywordTiersSelector = (state: {
  keywordTiers: KeywordTiersState;
}) => state.keywordTiers;

export const keywordsTiersReducer = keywordTiersSlice.reducer;
